import { Injectable } from '@angular/core';
import { mergeMap, Observable, of, throwError } from 'rxjs';
import { HttpService } from './http.service';
import { ApiResponse } from 'src/app/shared/models/api-response.interface';
import { RequestInspector } from 'src/app/shared/models/request-inspector.model';
import { ReopenRequest } from '../../../shared/models/reopen-request.interface';
import { ClaimPayment } from '../../../shared/models/claim-payment.interface';
import { Payment } from '../../../shared/models/payment.interface';
import { HttpHeaders, HttpParams } from '@angular/common/http';
import { VirtualCard } from '../../../shared/models/virtual-card.interface';
import { PaymentCalculation } from '../../../shared/models/payment-calculation.interface';
import { ProductLetterType } from '../../../shared/models/product-letter.interface';
import { ClaimAdjusterReportInterface } from '../../../shared/models/claim-adjuster-report.interface';
import { ClaimsCreatedReport } from '../../../shared/models/claims-created-report';
import { Params } from '@angular/router';
import { API_VERSIONS } from 'src/app/shared/consts/api-versions.const';
import { JdPowerData } from 'src/app/shared/models/jd-power-vehicle-info.interface';
import { isFailedApiResponse } from 'src/app/shared/utils/api-error-message.util';

@Injectable({
  providedIn: 'root',
})
export class ClaimApiService {
  private entityUrl: string = '/claim';

  constructor(private apiService: HttpService) {}

  private paymentResponse<T>(request: Observable<ApiResponse<T>>): Observable<ApiResponse<T>> {
    return request.pipe(
      mergeMap((response) => {
        if (isFailedApiResponse(response)) {
          return throwError(() => ({
            error: response,
            status: 400,
          }));
        }
        return of(response);
      })
    );
  }

  getClaimDetails(id: number): Observable<ApiResponse> {
    return this.apiService.getReq(`${this.entityUrl}/${id}`);
  }

  deleteClaim(id: number): Observable<ApiResponse> {
    return this.apiService.deleteReq(`${this.entityUrl}/${id}`);
  }
  sendPendingLetter(id: number): Observable<ApiResponse> {
    return this.apiService.getReq(
      `${this.entityUrl}/${id}/send-pending-letter`
    );
  }

  sendLetter(
    id: number,
    letterType: ProductLetterType
  ): Observable<ApiResponse> {
    return this.apiService.postReq(`${this.entityUrl}/${id}/send-letter`, {
      letter_type: letterType,
    });
  }
  getAllPaginated(params: any): Observable<ApiResponse> {
    return this.apiService.getReq(
      `/${API_VERSIONS.CLAIMS}${this.entityUrl}/paging`,
      {
        params: params,
      }
    );
  }

  getPrefixes(): Observable<ApiResponse> {
    return this.apiService.getReq('/prefix');
  }

  getCustomerStates(): Observable<ApiResponse> {
    return this.apiService.getReq('/claims/customer-states');
  }

  authorize(id: number): Observable<ApiResponse> {
    return this.apiService.getReq(`${this.entityUrl}/${id}/authorize`);
  }

  pay(id: number): Observable<ApiResponse> {
    return this.paymentResponse(this.apiService.getReq(`${this.entityUrl}/${id}/pay`));
  }

  requestInspector(request: RequestInspector): Observable<ApiResponse> {
    return this.apiService.postReq(
      `${this.entityUrl}s/${request.id}/inspection-request`,
      request
    );
  }

  getInspectors(id: number, inspectService?: string): Observable<ApiResponse> {
    return this.apiService.getReq(
      `${this.entityUrl}s/${id}/inspectors?${
        inspectService ? `inspect_service=${inspectService}` : ''
      }`
    );
  }

  getInspectionsRequests(id: number): Observable<ApiResponse> {
    return this.apiService.getReq(`${this.entityUrl}s/${id}/inspections`);
  }

  getInspectionsReport(id: number): Observable<ApiResponse> {
    return this.apiService.getReq(`${this.entityUrl}s/${id}/inspection-report`);
  }

  // getVirtualCards(claimId: number): Observable<ApiResponse> {
  //   return this.apiService.getReq(`${this.entityUrl}s/${claimId}/cards`);
  // }

  updateSettings(claimId: number, payload: any): Observable<ApiResponse> {
    return this.apiService.putReq(`${this.entityUrl}/${claimId}`, payload);
  }

  updateDateOfClaim(claimId: number, date: string): Observable<ApiResponse> {
    return this.apiService.putReq(`/date-of-claim/${claimId}`, {
      date_of_claim: date,
    });
  }

  payByWex(payload: ClaimPayment): Observable<ApiResponse> {
    return this.paymentResponse(this.apiService.postReq('/wex/payments', payload));
  }

  previewPayment(claimId: number, payload: any): Observable<ApiResponse> {
    return this.paymentResponse(
      this.apiService.postReq(`${this.entityUrl}s/${claimId}/payments/preview`, payload)
    );
  }

  getAdjustableCards(claimId: number, cardId?: string): Observable<ApiResponse> {
    const options = cardId ? { params: { card_id: cardId } } : undefined;
    return this.apiService.getReq(`${this.entityUrl}s/${claimId}/payments/adjustable-cards`, options);
  }

  getAttachableCards(claimId: number, thirdPartyId?: number, search?: string): Observable<ApiResponse> {
    const params: Record<string, string | number> = {};
    if (thirdPartyId) params['third_party_id'] = thirdPartyId;
    if (search?.trim()) params['search'] = search.trim();
    return this.apiService.getReq(
      `${this.entityUrl}s/${claimId}/payments/attachable-cards`,
      { params }
    );
  }

  getPaymentReverts(claimId: number): Observable<ApiResponse> {
    return this.apiService.getReq(`${this.entityUrl}s/${claimId}/payments/reverts`);
  }

  createClaimPayment(claimId: number, payload: any): Observable<ApiResponse> {
    return this.paymentResponse(
      this.apiService.postReq(`${this.entityUrl}s/${claimId}/payments`, payload)
    );
  }

  revertClaimPayment(
    claimId: number,
    cardId: string,
    payload: {
      reason?: string;
      notify: boolean;
      status_id?: number;
      deductible_amount?: number;
      quotes?: { quote_id: number; quote_type: string }[];
      revert_deductible?: boolean;
    }
  ): Observable<ApiResponse> {
    return this.paymentResponse(
      this.apiService.postReq(
        `${this.entityUrl}s/${claimId}/payments/${encodeURIComponent(cardId)}/revert`,
        payload
      )
    );
  }

  getClaimCards(claimId: number, params?: Record<string, number | string>): Observable<ApiResponse> {
    return this.apiService.getReq(`${this.entityUrl}s/${claimId}/cards`, { params });
  }

  createCustomCard(claimId: number, payload: any): Observable<ApiResponse> {
    return this.paymentResponse(
      this.apiService.postReq(`${this.entityUrl}s/${claimId}/custom-cards`, payload)
    );
  }

  transferCardLeftover(
    claimId: number,
    cardId: string | number,
    amountSource: string,
    payload: any
  ): Observable<ApiResponse> {
    const collection = amountSource === 'manual_entry' ? 'custom-cards' : 'payments';
    return this.paymentResponse(
      this.apiService.postReq(
        `${this.entityUrl}s/${claimId}/${collection}/${encodeURIComponent(String(cardId))}/transfer-leftover`,
        payload
      )
    );
  }

  getFeatherCardOrganizations(): Observable<ApiResponse> {
    return this.apiService.getReq('/feather-card/organizations');
  }

  createSourcedPartPayment(claimId: number, payload: any): Observable<ApiResponse> {
    return this.paymentResponse(
      this.apiService.postReq(`${this.entityUrl}s/${claimId}/create-sourced-part-payment`, payload)
    );
  }

  createQuotePayment(claimId: number, payload: any): Observable<ApiResponse> {
    return this.paymentResponse(
      this.apiService.postReq(`${this.entityUrl}s/${claimId}/create-quote-payment`, payload)
    );
  }

  getPaymentCalculation(
    payload: ClaimPayment
  ): Observable<ApiResponse<PaymentCalculation>> {
    return this.paymentResponse(this.apiService.postReq('/wex/payment-calculation', payload));
  }

  getVirtualCards(claimId: number): Observable<ApiResponse<VirtualCard[]>> {
    const params = new HttpParams()
      .set('payable_id', claimId)
      .set('payable_type', 'claim');
    return this.apiService.getReq('/wex/virtual-cards', { params });
  }

  getWexPayments(): Observable<ApiResponse<Payment[]>> {
    return this.apiService.getReq('/wex/payments');
  }

  getPaidWexPayments(
    payable_id: number,
    payable_type: string
  ): Observable<ApiResponse<Payment[]>> {
    const params = new HttpParams()
      .set('payable_id', payable_id)
      .set('payable_type', payable_type);
    return this.apiService.getReq(`/wex/transactions-by-payable`, { params });
  }

  sendReopenClaimRequest(
    claimId: number,
    reason: string
  ): Observable<ApiResponse> {
    return this.apiService.postReq(
      `${this.entityUrl}s/${claimId}/reopen-requests`,
      {
        claimId,
        reason,
      }
    );
  }

  updateReopenClaimRequest(
    claimReopenId: number,
    claimId: number,
    status: string
  ): Observable<ApiResponse> {
    return this.apiService.putReq(
      `${this.entityUrl}s/${claimId}/reopen-requests/${claimReopenId}`,
      {
        claimId,
        claimReopenId,
        status,
      }
    );
  }

  getReopenClaimRequests(
    claimId: number
  ): Observable<ApiResponse<ReopenRequest[]>> {
    return this.apiService.getReq(
      `${this.entityUrl}s/${claimId}/reopen-requests`
    );
  }

  exportClaimPdf(claimId: number): Observable<any> {
    return this.apiService.postReq(`${this.entityUrl}/${claimId}/pdf`);
  }

  exportClaimInfoPdf(claimId: number): Observable<any> {
    return this.apiService.getReq(
      `/${API_VERSIONS.CLAIMS}${this.entityUrl}/${claimId}/pdf`
    );
  }

  addDenialReason(
    claimId: number,
    payload: { denial_reason: string; denial_reference: string }
  ) {
    return this.apiService.postReq(
      `${this.entityUrl}s/${claimId}/denial-reason`,
      payload
    );
  }

  copyMilestonesFromProduct(claimId: number, productId: number) {
    return this.apiService.postReq(
      `${this.entityUrl}/${claimId}/copy-claim-milestone-from-product`,
      { product_id: productId }
    );
  }

  getAdjusterReports(
    params?: Params
  ): Observable<ApiResponse<ClaimAdjusterReportInterface[]>> {
    return this.apiService.getReq(`${this.entityUrl}s/adjusters/reports`, {
      params,
    });
  }

  getClaimsCreatedReports(
    params?: Params
  ): Observable<ApiResponse<ClaimsCreatedReport[]>> {
    return this.apiService.getReq(`${this.entityUrl}s/creators/reports`, {
      params,
    });
  }

  getClaimLossCauses(): Observable<ApiResponse> {
    return this.apiService.getReq(`${this.entityUrl}/causes`);
  }

  assignAgentToDealersClaims(agent_id: number): Observable<ApiResponse> {
    return this.apiService.postReq(
      `${this.entityUrl}s/assign-agent-to-dealers-claims`,
      { agent_id }
    );
  }

  updateClaimActualCashValue(
    claimId: number,
    actual_cash_value: number
  ): Observable<ApiResponse> {
    return this.apiService.putReq(
      `${this.entityUrl}s/${claimId}/actual-cash-value`,
      { actual_cash_value }
    );
  }

  exportLienholderClaims(lien_holder_id: number): Observable<any> {
    return this.apiService.getReq(
      `${this.entityUrl}s/lienholder-export?lien_holder_id=${lien_holder_id}`,
      { responseType: 'text' }
    );
  }

  getJdPowerData(
    claimId: number,
    pull: boolean = false,
    mileage?: number,
    region?: string,
    period?: string
  ): Observable<ApiResponse<JdPowerData>> {
    const params: any = { pull };
    // Always include mileage and region when provided (even if 0 or "1")
    if (mileage !== undefined) {
      params.mileage = mileage;
    }
    if (region !== undefined) {
      params.region = region;
    }
    if (period !== undefined) {
      params.period = period;
    }
    console.log('API call params:', params);
    return this.apiService.getReq(
      `${this.entityUrl}/${claimId}/jd-power-data`,
      { params }
    );
  }

  getJdPowerRegions(refresh: boolean = false): Observable<ApiResponse<any>> {
    return this.apiService.getReq(
      `/jd-power/regions`,
      { params: { refresh } }
    );
  }

  exportJdPowerDataPdf(claimId: number): Observable<ApiResponse> {
    return this.apiService.getReq(`${this.entityUrl}/${claimId}/jd-power-pdf`);
  }

  toggleIsFortegraClaim(claimId: number): Observable<ApiResponse> {
    return this.apiService.putReq(`${this.entityUrl}/${claimId}/toggle-is-fortegra-claim`);
  }

  toggleIsLegalClaim(claimId: number): Observable<ApiResponse> {
    return this.apiService.putReq(
      `${this.entityUrl}/${claimId}/toggle-is-legal-claim`
    );
  }

  toggleIsException(claimId: number): Observable<ApiResponse> {
    return this.apiService.putReq(
      `${this.entityUrl}/${claimId}/toggle-is-exception`
    );
  }

  sendToAssistara(claimId: number): Observable<ApiResponse> {
    return this.apiService.postReq(`${this.entityUrl}s/${claimId}/send-to-assistara`);
  }

  getCoverageAnalysis(claimId: number): Observable<ApiResponse> {
    return this.apiService.getReq(`${this.entityUrl}s/${claimId}/coverage-analysis`);
  }

  getZipDocuments(claimId: number): Observable<ApiResponse> {
    return this.apiService.getReq(`${this.entityUrl}s/${claimId}/zip-documents`);
  }
}
